import { AndOrOperator } from '~/client/src/shared/models/LogicOperation'
import OperationRule, {
  COMPANY_TAG_TYPE,
  COMPANY_TYPE_TAG_TYPE,
} from '~/client/src/shared/models/OperationRule'
import User from '~/client/src/shared/models/User'

export type FilterCategory = 'company' | 'companyType'

export interface IFilterGroup {
  category: FilterCategory
  isNot: boolean
  valueIds: string[]
}

export interface IRuleFilterState {
  filters: IFilterGroup[]
  connectors: AndOrOperator[] // length = filters.length - 1
}

const TAG_REGEX = /\[(\w+)\]\[([^\]]+)\]/g

function categoryToTagType(category: FilterCategory): string {
  return category === 'company' ? COMPANY_TAG_TYPE : COMPANY_TYPE_TAG_TYPE
}

function tagTypeToCategory(tagType: string): FilterCategory | null {
  if (tagType === COMPANY_TAG_TYPE) return 'company'
  if (tagType === COMPANY_TYPE_TAG_TYPE) return 'companyType'
  return null
}

export function buildExpression(state: IRuleFilterState): string {
  // Collect active (non-empty) filters with their original indices
  const active: { filter: IFilterGroup; originalIdx: number }[] = []
  for (let i = 0; i < state.filters.length; i++) {
    if (state.filters[i].valueIds.length > 0) {
      active.push({ filter: state.filters[i], originalIdx: i })
    }
  }

  if (active.length === 0) return ''

  const groups = active.map(({ filter }) => {
    const tagType = categoryToTagType(filter.category)
    const tags = filter.valueIds
      .map(id => OperationRule.makeTagExpressionString(tagType, id))
      .join(` ${AndOrOperator.OR} `)
    return filter.isNot ? `! ( ${tags} )` : `( ${tags} )`
  })

  let result = groups[0]
  for (let i = 1; i < groups.length; i++) {
    // Use the connector immediately after the previous active filter
    const connectorIdx = active[i].originalIdx - 1
    const connector =
      connectorIdx >= 0 && connectorIdx < state.connectors.length
        ? state.connectors[connectorIdx]
        : AndOrOperator.AND
    result += ` ${connector} ${groups[i]}`
  }
  return result
}

// Regex to match parenthesized groups, optionally preceded by !
const GROUP_REGEX = /(!?\s*\([^)]*\))/g

export function parseExpression(expression: string): IRuleFilterState {
  if (!expression || !expression.trim()) {
    return { filters: [], connectors: [] }
  }

  const filters: IFilterGroup[] = []
  const connectors: AndOrOperator[] = []

  const groupMatches = [...expression.matchAll(GROUP_REGEX)]
  if (groupMatches.length === 0) {
    return { filters: [], connectors: [] }
  }

  for (let i = 0; i < groupMatches.length; i++) {
    const match = groupMatches[i]
    const groupStr = match[0].trim()

    const isNot = groupStr.startsWith('!')
    const bracketContent = groupStr.slice(
      groupStr.indexOf('(') + 1,
      groupStr.lastIndexOf(')'),
    )

    const tagRegex = new RegExp(TAG_REGEX.source, 'g')
    let tagMatch: RegExpExecArray | null
    const valueIds: string[] = []
    let category: FilterCategory | null = null

    while ((tagMatch = tagRegex.exec(bracketContent)) !== null) {
      const [, tagType, tagId] = tagMatch
      const cat = tagTypeToCategory(tagType)
      if (cat) {
        if (!category) category = cat
        valueIds.push(tagId)
      }
    }

    if (category && valueIds.length > 0) {
      filters.push({ category, isNot, valueIds })

      // Extract connector between this group and previous
      if (i > 0) {
        const prevEnd =
          groupMatches[i - 1].index + groupMatches[i - 1][0].length
        const between = expression.slice(prevEnd, match.index)
        if (between.trim() === AndOrOperator.OR) {
          connectors.push(AndOrOperator.OR)
        } else {
          connectors.push(AndOrOperator.AND)
        }
      }
    }
  }

  return { filters, connectors }
}

export function getUsersFromAssignment(
  assignmentExpressions: string[],
  getById: (id: string) => User | undefined,
): User[] {
  if (!assignmentExpressions?.length) {
    return []
  }

  const tagStartSymbol = '['
  return assignmentExpressions.reduce<User[]>((users, element) => {
    if (!element.includes(tagStartSymbol)) {
      return users
    }

    const match = element.match(/\[(\w+)\]\[([^\]]+)\]/)
    if (!match) {
      return users
    }

    const [, , tagValue] = match
    const user = getById(tagValue)
    return user ? [...users, user] : users
  }, [])
}
